import { useEffect } from "react";
import { Button, Result } from "antd";
import { Link, useLocation, useRouteError } from "react-router-dom";
import {
  captureClientError,
  maybeReloadOnChunkError,
} from "../../utils/clientEvents";

// Route-level error boundary for the livestream admin pages.
//
// WHY: no route in client/src/index.tsx declared an `errorElement`, so ANY
// render-time throw — a malformed Relay payload, a null deref in a chart, the
// `SyntaxError` a non-JSON /graphql response used to raise (now caught in
// relayEnvironment.ts) — fell through to React Router's stock boundary. That
// boundary renders a bare white page reading "Unexpected Application Error!"
// with the raw message underneath and NO way back into the app: the operator
// has to know to edit the URL, mid-broadcast.
//
// ANTD, NOT MANTINE, deliberately. Each livestream page mounts its own LOCAL
// AdminMantineProvider *inside* its element (see LivestreamOverviewPage.tsx and
// CLAUDE.md's UI-library note) — an errorElement renders INSTEAD of that
// element, so the Mantine provider it would need is exactly the thing that
// isn't mounted. antd's providers sit above the router, so they are always
// there. Rendering a Mantine component here would work only by accident of
// default theming, and would break the moment the page's provider mattered.
//
// Deliberately SELF-CONTAINED: no DashboardContainer wrapper. An error boundary
// that mounts the shell's provider stack (RealtimeProvider, the presence
// heartbeat, the notifications bell) can throw a second time from the very
// layer that may have thrown first. A boundary has to be the simplest
// component on the page.
//
// IT ALSO REPORTS AND SELF-HEALS. A route errorElement consumes the throw inside
// RouterProvider's own internal boundary, so a crash here never reaches the root
// <ErrorBoundary> (the only other thing that calls captureClientError) nor
// window.onerror. handleAdminRouteError below therefore reports every crash
// through captureClientError (`source: "route_error_boundary"`) and routes
// chunk-load failures through maybeReloadOnChunkError — a tab left open across
// a deploy whose hashed chunks were pruned reloads itself instead of parking the
// operator on this screen (10 s debounce, two attempts per tab, 5-min reset: the
// same guard the root ErrorBoundary and LiveRouteError use). Every other error
// is a no-op there, which leaves the manual Reload button as the fallback.

/** Longest error text shown. Past this it is a stack, not a message. */
const MAX_MESSAGE_CHARS = 300;

/**
 * React Router hands `unknown` — a thrown Error, a thrown string, a Response
 * from a loader, or anything else the page managed to throw. Reduce all of it
 * to one readable line.
 */
function describeRouteError(error: unknown): string {
  if (!error) return "Unknown error.";
  if (typeof error === "string") return error;
  if (error instanceof Error) return error.message || error.name;
  const maybeMessage = (error as { message?: unknown }).message;
  if (typeof maybeMessage === "string" && maybeMessage) return maybeMessage;
  const maybeStatus = error as { status?: unknown; statusText?: unknown };
  if (typeof maybeStatus.status === "number") {
    return `HTTP ${maybeStatus.status}${
      maybeStatus.statusText ? ` ${maybeStatus.statusText}` : ""
    }`;
  }
  try {
    return JSON.stringify(error);
  } catch {
    return String(error);
  }
}

/**
 * Report the crash, then let the shared chunk-error handler decide whether this
 * is a stale tab. Order matters: maybeReloadOnChunkError reloads the page when
 * it recognises a hashed-chunk 404 (a tab left open across a deploy whose
 * chunks were pruned), and capture must have run first. It is a no-op for every
 * other error, which leaves the manual Reload button below as the fallback.
 * Each step is wrapped so reporting can never be the SECOND thing that throws
 * on this screen, and a failed self-heal degrades to the button.
 */
function handleAdminRouteError(
  error: unknown,
  route: string,
  message: string,
): void {
  try {
    // `routeMessage` is the readable line describeRouteError produced: for a
    // non-Error throw (a React Router ErrorResponse) captureClientError would
    // otherwise record "[object Object]" as the message.
    captureClientError(error, {
      source: "route_error_boundary",
      route,
      routeMessage: message,
    });
  } catch {
    // never let reporting cost the self-heal below
  }
  try {
    maybeReloadOnChunkError(error, message);
  } catch {
    // a failed self-heal degrades to the Reload button
  }
}

const AdminRouteError = () => {
  const error = useRouteError();
  const { pathname } = useLocation();
  const message = describeRouteError(error).slice(0, MAX_MESSAGE_CHARS);

  useEffect(() => {
    handleAdminRouteError(error, pathname, message);
  }, [error, pathname, message]);

  return (
    <div
      style={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 24,
      }}
    >
      <Result
        status="error"
        title="This page hit an error"
        subTitle={
          <pre
            style={{
              // The message can be a long single line (a GraphQL error, a URL);
              // wrapping keeps it inside the card instead of forcing the page
              // to scroll sideways.
              whiteSpace: "pre-wrap",
              wordBreak: "break-word",
              textAlign: "left",
              margin: "12px auto 0",
              maxWidth: 640,
              fontSize: 12,
              lineHeight: 1.5,
            }}
          >
            {message}
          </pre>
        }
        extra={[
          // A full reload, not a re-render: whatever state produced the throw
          // is still in the store, so re-mounting the same tree usually throws
          // again.
          <Button
            key="reload"
            type="primary"
            onClick={() => window.location.reload()}
          >
            Reload
          </Button>,
          <Link key="planning" to="/admin/livestream/planning">
            <Button>Back to Stream planning</Button>
          </Link>,
        ]}
      />
    </div>
  );
};

export default AdminRouteError;
